import React, { useCallback } from 'react';
import Head from 'next/head';
import I18N from '@/services/I18N';
import { setAppLocale } from '@/store/slices/app';
import { getISOLocale } from '@/models/locale';
import useAllCoaches from '@/hooks/allCoaches';
import BaseLayout from '../../../layouts/BaseLayout';
import LayoutWithNav from '../../../layouts/LayoutWithNav';
import { wrapper } from '../../../store';
import AuraContent from '../../../services/AuraContent';
import {
  getMeditation,
  getMeditationPhoto,
  getMeditationIdFromSlug,
  getChannelAuthorName,
} from '../../../models/meditation';
import { getCoach, getCoachName } from '../../../models/coach';
import routeConstants from '../../../utils/constants/routes';
import Loader from '../../../components/app/Loader';
import { formatSecondsAsTime } from '../../../utils';
import urlFormater from '../../../utils/urlFormater';
import { getChannel } from '../../../models/channel';
import usePageQuery from '../../../hooks/pageQuery';
import TrackMeditationSlug from '../../../components/page/track/[slugMeditation]';

async function fetchPageData(query, locale) {
  if (!query[routeConstants.SLUG_MEDITATION]) {
    return {
      error: 'Track slug not found',
    };
  }
  let trackId = await getMeditationIdFromSlug(
    query[routeConstants.SLUG_MEDITATION]
  );
  if (!trackId) {
    trackId = urlFormater.getIDFromURL(query[routeConstants.SLUG_MEDITATION]);
  }
  if (!trackId) {
    return { error: 'trackId is not valid' };
  }
  const track = await getMeditation(trackId);
  if (!track) {
    return {
      error: 'track not found',
    };
  }
  const relatedTracks = await AuraContent.getTracks({
    routeSuffix: `contents/${track.session}/related`,
    overrideDurationFilter: 4,
    overrideContentType: `all`,
    limit: 20,
    locale: 'all',
    preferredLocale: locale,
  });

  const coach = await getCoach(track.userId);
  const channel = track.channel ? await getChannel(track.channel.key) : null;
  return {
    track,
    relatedTracks,
    coach,
    channel,
  };
}

function getMetaDescription(track, coach) {
  const { content, contentShort, title } = track || {};
  if (contentShort) return contentShort;
  if (content) return content.replace(/\n/g, ' ');
  return `Listen to ${title} by ${
    coach ? getCoachName(coach) : getChannelAuthorName(track)
  } on Aura.`;
}

function Meditation(serverProps) {
  const { track, relatedTracks, coach, channel } = serverProps;
  const { userId } = usePageQuery({ fetchUserFromQuery: true });
  const { title, duration, slug } = track || {};
  useAllCoaches();

  const seoTrackDesc = useCallback(() => {
    let desc = getMetaDescription(track, coach);
    if (desc.length <= 33) {
      desc = `${desc} Listen to this track and much more on Aura - the #1 Mindfulness, Life Coaching and Sleep App`;
    } else if (desc.length <= 90) {
      desc = `${desc} Listen to this track and much more on Aura - the #1 Mindfulness App`;
    } else if (desc.length <= 99) {
      desc = `${desc} Listen to this track and much more on Aura.`;
    }
    return desc;
  }, [coach, track]);

  const seoTrackTitle = useCallback(() => {
    let pageTitle = `${title} by ${getCoachName(coach)} (${formatSecondsAsTime(
      duration,
      {
        isString: true,
      }
    )}) - Aura`;
    if (pageTitle.length > 70) {
      pageTitle = `${title} (${formatSecondsAsTime(duration, {
        isString: true,
      })}) - Aura`;
    }
    if (pageTitle.length > 70) {
      pageTitle = `${title} - Aura`;
    }
    if (pageTitle.length > 70) {
      pageTitle = `${title}`;
    }
    return pageTitle;
  }, [title, coach, duration]);

  if (!track) {
    return (
      <BaseLayout>
        <Loader />
      </BaseLayout>
    );
  }

  return (
    <LayoutWithNav isUserFromQuery={!!userId} showSEOFooter>
      <Head>
        <title>{seoTrackTitle()}</title>
        <meta name="description" content={seoTrackDesc()} />
        <meta property="og:title" content={seoTrackTitle()} />
        <meta
          itemProp="image"
          property="og:image"
          content={getMeditationPhoto(track)}
        />
        <meta
          property="og:url"
          content={`${process.env.NEXT_PUBLIC_APP_DOMAIN}/${routeConstants.PAGE_TRACK}/${slug}`}
        />
        <meta property="og:description" content={seoTrackDesc()} />
        <meta name="twitter:card" content="summary_large_image" />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_APP_DOMAIN}/${routeConstants.PAGE_TRACK}/${slug}`}
        />
      </Head>
      <TrackMeditationSlug
        track={track}
        channel={channel}
        coach={coach}
        relatedTracks={relatedTracks}
      />
    </LayoutWithNav>
  );
}

export const getServerSideProps = wrapper.getServerSideProps(
  (store) =>
    async ({ params, res, locale }) => {
      res.setHeader(
        'Cache-Control',
        `public, s-maxage=${60 * 60 * 24}, stale-while-revalidate=${
          60 * 60 * 24
        }`
      );
      let props = await fetchPageData(params, getISOLocale(locale));
      if (props.track) {
        props.key = props.track.slug;
      }
      const notFound = !!props.error;

      await store.dispatch(setAppLocale(getISOLocale(locale)));
      props = {
        ...props,
        ...(await I18N.loadLocale({
          locale,
          route: '/track/[slugMeditation]',
        })),
      };
      return { props, notFound };
    }
);

export default Meditation;
